import React from 'react'

import { useWindowEvent } from '@mantine/hooks'
import { Mark } from 'prosemirror-model'

import { useMst } from '#state'
import { selectionHeadPosition } from '#util'

import {
  addOrEditLinkMark,
  getLinkForSelection,
  removeLinkMark,
} from './getLinkForSelection'

export const useInlineHyperlink = () => {
  const { view, currentScript } = useMst()
  const ref = React.useRef<HTMLElement | null>(null)

  const editorView = currentScript?.observableEditor?.editorView
  const selection = currentScript?.observableEditor?.textSelection
  const editorViewElt = currentScript?.observableEditor?.editorView?.dom
  const isEditing = currentScript?.observableEditor?.editingHyperlink
  const setEditing = (value: boolean) =>
    currentScript?.observableEditor?.setEditingHyperlink(value)

  React.useEffect(() => {
    if (editorViewElt instanceof HTMLElement) {
      ref.current = editorViewElt
    } else {
      ref.current = null
    }
  }, [editorViewElt])

  const [mouseDown, setMouseDown] = React.useState(false)
  const [shiftDown, setShiftDown] = React.useState(false)

  useWindowEvent('mousedown', () => {
    setMouseDown(true)
  })

  useWindowEvent('mouseup', () => {
    setMouseDown(false)
  })

  useWindowEvent('keydown', (e) => {
    setShiftDown(e.shiftKey)
  })

  useWindowEvent('keyup', (e) => {
    if (shiftDown && !e.shiftKey) {
      setShiftDown(false)
    }
  })

  const userSelecting = (mouseDown || shiftDown) && editorView?.hasFocus()

  const currentHyperlink = editorView
    ? getLinkForSelection(editorView)
    : undefined

  const headPosition = React.useMemo(
    () =>
      editorView && selection
        ? selectionHeadPosition({
            editorView,
            selection,
            zoomLevel: view.editorZoom,
          })
        : undefined,
    [editorView, selection, view.editorZoom],
  )

  const removeCurrentLink = () => {
    if (currentHyperlink && editorView) {
      removeLinkMark({ mark: currentHyperlink, editorView })
    }
  }

  const addOrEditLink = (text: string, mark?: Mark) =>
    addOrEditLinkMark({ text, mark, editorView })

  // Refresh the positioning anchor when selection changes. Keep the popover
  // mounted so edits that map the selection cannot discard an open link draft.
  const targetKey = `link-pos-${selection?.$head.pos ?? 0}`

  return {
    userSelecting,
    headPosition,
    currentHyperlink,
    isEditing,
    addOrEditLink,
    removeCurrentLink,
    setEditing,
    targetKey,
  }
}
